import type { StatusSolicitacao } from '@/features/solicitacoes/status';
import { ehDemandaReprimida, type OrigemSolicitacao } from '@/features/solicitacoes/origem';
import type { PerfilUsuario } from '@/features/auth/tipos';

/**
 * Espelho de `backend/src/solicitacoes/edicao.ts`.
 *
 * Existe para a tela não oferecer um botão que o servidor vai recusar — e para
 * dizer o porquê no lugar dele. Quem decide de verdade é o backend: qualquer
 * divergência aqui vira mensagem de erro, não permissão indevida.
 */

/** Status em que a solicitação ainda aceita correção de dados. */
export function podeEditar(status: StatusSolicitacao): boolean {
  return status !== 'AGENDADO';
}

/**
 * Perfis que corrigem dados.
 *
 * A UBS fala com o paciente e recebe a requisição do médico; a Regulação
 * descobre a divergência ao analisar o caso; gestão e administração entram pela
 * mesma razão que decidem cancelamento de solicitação não agendada.
 *
 * Na demanda reprimida a UBS fica de fora: o pedido é anterior ao sistema e
 * não foi ela quem o registrou — o papel está na secretaria, com quem confere.
 *
 * O segundo parâmetro não tem valor padrão, como no backend. Um default faria
 * o chamador esquecido compilar em silêncio, e o silêncio aqui mostraria à UBS
 * um botão que o servidor recusa.
 */
export function podeEditarPerfil(perfil: PerfilUsuario, origem: OrigemSolicitacao): boolean {
  if (ehDemandaReprimida(origem)) {
    return perfil === 'REGULACAO' || perfil === 'GESTOR' || perfil === 'ADMINISTRADOR';
  }

  return (
    perfil === 'UBS' || perfil === 'REGULACAO' || perfil === 'GESTOR' || perfil === 'ADMINISTRADOR'
  );
}

/**
 * Por que a edição não está disponível — dito com o motivo, não só o "não".
 *
 * A origem do agendamento importa: o texto padrão afirma que o paciente foi
 * avisado e que o pedido existe no eSUS, e as duas coisas são **falsas** na
 * demanda reprimida declarada já agendada fora do KAVI. O bloqueio é o mesmo;
 * a explicação não pode ser.
 */
export function explicarSemEdicao(
  status: StatusSolicitacao,
  agendamentoOrigem: 'ESUS' | 'MANUAL' | 'ANTERIOR' | null,
): string {
  if (status !== 'AGENDADO') return 'Esta solicitação não aceita edição no momento.';

  if (agendamentoOrigem === 'ANTERIOR') {
    return (
      'Esta demanda reprimida foi declarada já agendada fora do KAVI: o caso está encerrado ' +
      'e registros agendados não aceitam correção de dados. Se a declaração foi um engano, o ' +
      'caminho é a reabertura, que é da Regulação.'
    );
  }

  return (
    'Solicitação agendada não é editada: o paciente já foi avisado e o pedido existe no eSUS. ' +
    'Para mudar o que foi agendado, o caminho é o cancelamento ou a reabertura, que são da Regulação.'
  );
}

/** Por que este perfil não corrige este registro. */
export function explicarSemEdicaoPerfil(perfil: PerfilUsuario, origem: OrigemSolicitacao): string {
  if (ehDemandaReprimida(origem) && perfil === 'UBS') {
    return (
      'Esta é uma demanda reprimida: o pedido é anterior ao KAVI e foi registrado pela ' +
      'Regulação a partir do papel que está na secretaria. A correção cabe a quem tem esse ' +
      'papel em mãos. Você pode acompanhar o andamento normalmente.'
    );
  }

  return 'Seu perfil não corrige os dados de uma solicitação.';
}
